import { useState } from "react";
import { SettingsSection } from "./setting/SettingsSection";
import { SettingsRow } from "./setting/SettingsRow";
import { Switch } from "@sphinx/design-system/switch";
import { Button } from "@sphinx/design-system/button";
import { Input } from "@sphinx/design-system/input";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";
import { useAiProviderKeys } from "@/hooks/use-ai-provider-keys";
import { updateThemeMode } from "@/lib/theme-utils";
import type { ModelProvider } from "@/shared/types";

interface SettingsTabProps {
  activeOrganizationId?: string;
  onNavigate?: (section: string) => void;
}

/**
 * The sphinx server holds ANTHROPIC_API_KEY / OPENAI_API_KEY and every chat
 * and eval route falls back to them when a request carries no key. These
 * fields are therefore OPTIONAL overrides: empty means "use the server's
 * key", and only a non-empty value is ever sent with a request.
 */
const PROVIDER_OVERRIDES: { id: ModelProvider; name: string; placeholder: string }[] =
  [
    { id: "anthropic", name: "Anthropic", placeholder: "sk-ant-..." },
    { id: "openai", name: "OpenAI", placeholder: "sk-..." },
  ];

function ApiKeyOverrideRow({
  name,
  placeholder,
  stored,
  onSave,
}: {
  name: string;
  placeholder: string;
  /** The persisted override ("" = none, the server's key is used). */
  stored: string;
  onSave: (next: string) => void;
}) {
  const [draft, setDraft] = useState(stored);
  const dirty = draft.trim() !== stored;

  return (
    <SettingsRow
      label={name}
      value={
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const next = draft.trim();
            onSave(next);
            setDraft(next);
          }}
        >
          <div className="flex flex-col items-end gap-1">
            <Input
              type="password"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={placeholder}
              aria-label={`${name} API key override`}
              className="h-8 w-64"
            />
            <span className="text-xs text-muted-foreground">
              {stored ? "Overriding the server's key" : "Using the server's key"}
            </span>
          </div>
          <Button type="submit" size="sm" disabled={!dirty}>
            Save
          </Button>
          {stored ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                onSave("");
                setDraft("");
              }}
            >
              Clear
            </Button>
          ) : null}
        </form>
      }
    />
  );
}

export function SettingsTab(_props: SettingsTabProps = {}) {
  const themeMode = usePreferencesStore((s) => s.themeMode);
  const setThemeMode = usePreferencesStore((s) => s.setThemeMode);
  const { getToken, setToken, clearToken } = useAiProviderKeys();

  const handleThemeToggle = (checked: boolean) => {
    const newTheme = checked ? "dark" : "light";
    updateThemeMode(newTheme);
    setThemeMode(newTheme);
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-5xl space-y-8 p-4 md:p-10">
        <h1 className="text-2xl font-semibold">Settings</h1>

        {/* About */}
        <SettingsSection title="About">
          <SettingsRow label="Version" value={`v${__APP_VERSION__}`} />
        </SettingsSection>

        {/* Appearance */}
        <SettingsSection title="Appearance">
          <SettingsRow
            label="Theme"
            value={
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {themeMode === "dark" ? "Dark" : "Light"}
                </span>
                <Switch
                  checked={themeMode === "dark"}
                  onCheckedChange={handleThemeToggle}
                  aria-label="Toggle dark mode"
                />
              </div>
            }
          />
        </SettingsSection>

        {/* API key overrides — optional; the server's keys are the default. */}
        <SettingsSection title="API key overrides">
          <p className="px-1 pb-1 text-xs text-muted-foreground">
            The server already holds Anthropic and OpenAI keys. Enter a key
            here only to override it for this browser; keys are stored locally.
          </p>
          {PROVIDER_OVERRIDES.map((p) => (
            <ApiKeyOverrideRow
              key={p.id}
              name={p.name}
              placeholder={p.placeholder}
              stored={getToken(p.id)}
              onSave={(next) =>
                next ? setToken(p.id, next) : clearToken(p.id)
              }
            />
          ))}
        </SettingsSection>
      </div>
    </div>
  );
}
